import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MessagesSquare, Send, Trash2, CornerDownRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Avatar, useAgo } from "@/components/dm/ui";
import { ReportButton } from "@/components/dm/ReportDialog";

const errText = (t, e) => t(e?.response?.data?.detail?.code || "error");

const AnswerForm = ({ q, onDone }) => {
  const { t } = useApp();
  const [text, setText] = useState("");
  const send = async () => {
    try { await api.post(`/questions/${q.id}/answer`, { answer: text }); setText(""); toast.success(t("q_answered")); onDone(); }
    catch (e) { toast.error(errText(t, e)); }
  };
  return (
    <div className="flex gap-2 pl-12">
      <input data-testid={`answer-input-${q.id}`} value={text} maxLength={500} onChange={(e) => setText(e.target.value)} placeholder={t("q_answer_ph")}
        className="flex-1 min-w-0 min-h-[48px] rounded-full border border-[#E8E2D9] bg-[#FAF7F2] px-4 text-base" />
      <button data-testid={`answer-submit-${q.id}`} disabled={!text.trim()} onClick={send} className="press min-h-[48px] px-5 rounded-full bg-[#1E4D3B] text-white font-bold disabled:opacity-40">{t("q_answer")}</button>
    </div>
  );
};

const QuestionItem = ({ q, p, isOwner, reload }) => {
  const { t, user } = useApp();
  const ago = useAgo();
  const canDelete = user && (user.id === q.asker?.id || isOwner || user.role === "admin");
  const del = async () => {
    if (!window.confirm(t("q_confirm_delete"))) return;
    try { await api.delete(`/questions/${q.id}`); toast.success(t("q_deleted")); reload(); } catch (e) { toast.error(errText(t, e)); }
  };
  return (
    <li data-testid={`question-${q.id}`} className="space-y-3 p-4 rounded-[20px] bg-[#FFFDF9] border border-[#E8E2D9]">
      <div className="flex gap-3">
        <Avatar user={q.asker || {}} size={40} />
        <div className="min-w-0 flex-1">
          <div className="text-sm"><span className="font-bold">{q.asker?.name}</span> <span className="text-[#6B5E55]">· {ago(q.created_at)}</span></div>
          <p className="text-base leading-snug break-words" data-testid={`question-body-${q.id}`}>{q.body}</p>
        </div>
      </div>
      {q.answer ? (
        <div className="flex gap-2 pl-12" data-testid={`question-answer-${q.id}`}>
          <CornerDownRight className="w-4 h-4 mt-1 text-[#1E4D3B] shrink-0" aria-hidden="true" />
          <div className="min-w-0 rounded-2xl bg-[#ECF3EF] px-4 py-2">
            <div className="text-xs font-extrabold text-[#133E2F] uppercase tracking-wide">{t("q_poster")} · {p.poster?.name}</div>
            <p className="text-base break-words">{q.answer}</p>
          </div>
        </div>
      ) : isOwner ? <AnswerForm q={q} onDone={reload} /> : <div className="pl-12 text-xs font-semibold text-[#9A3412]">{t("q_awaiting")}</div>}
      <div className="flex gap-2 pl-12 flex-wrap">
        {canDelete && <button data-testid={`question-delete-${q.id}`} onClick={del} className="press inline-flex items-center gap-1 min-h-[44px] px-3 rounded-full text-sm text-[#6B5E55]"><Trash2 className="w-4 h-4" />{t("q_delete")}</button>}
        {user?.id !== q.asker?.id && <ReportButton itemType="question" itemId={q.id} testid={`question-report-${q.id}`} label={t("r_question")} />}
      </div>
    </li>
  );
};

export const Questions = ({ p, isOwner }) => {
  const { t, requireVerified } = useApp();
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const ref = useRef(null);
  const load = () => api.get(`/posts/${p.id}/questions`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [p.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (items && window.location.hash === "#questions") ref.current?.scrollIntoView({ behavior: "smooth" }); }, [items]);
  const ask = async () => {
    if (!requireVerified()) return;
    try { await api.post(`/posts/${p.id}/questions`, { body: text }); setText(""); toast.success(t("q_sent")); load(); }
    catch (e) { toast.error(errText(t, e)); }
  };
  return (
    <section ref={ref} id="questions" data-testid="questions-section" className="space-y-3 scroll-mt-20">
      <div>
        <h2 className="text-base md:text-lg font-extrabold inline-flex items-center gap-2"><MessagesSquare className="w-5 h-5 text-[#1E4D3B]" aria-hidden="true" />{t("q_title")}{items?.length ? ` (${items.length})` : ""}</h2>
        <p className="text-sm text-[#6B5E55]">{isOwner ? t("q_owner_hint") : t("q_sub")}</p>
      </div>
      {p.is_demo ? <p className="text-sm text-[#6B5E55]" data-testid="questions-demo-note">{t("q_no_demo")}</p> : !isOwner && (
        <div className="flex gap-2">
          <input data-testid="question-input" value={text} maxLength={300} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && text.trim().length >= 3 && ask()}
            placeholder={t("q_placeholder")} className="flex-1 min-w-0 min-h-[52px] rounded-full border border-[#E8E2D9] bg-[#FFFDF9] px-5 text-base" />
          <button data-testid="question-submit" aria-label={t("q_ask")} disabled={text.trim().length < 3} onClick={ask}
            className="press min-h-[52px] px-5 rounded-full bg-[#F59E0B] text-[#291E18] font-extrabold inline-flex items-center gap-2 disabled:opacity-40"><Send className="w-4 h-4" />{t("q_ask")}</button>
        </div>
      )}
      {items === null ? <div className="skeleton h-16 rounded-2xl" /> : items.length === 0 ? (!p.is_demo && <p className="text-sm text-[#6B5E55]" data-testid="questions-empty">{t("q_none")}</p>) : (
        <ul className="space-y-3">{items.map((q) => <QuestionItem key={q.id} q={q} p={p} isOwner={isOwner} reload={load} />)}</ul>
      )}
    </section>
  );
};
